import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// ── Kill switch ──────────────────────────────
// Flip to `true` to re-enable freewall popups.
const FREEWALL_ENABLED = false;
// ─────────────────────────────────────────────

// ── Popup intensity knobs ──
const FIRST_TRIGGER_MS   = 180000;
const MASTER_INTERVAL_MS = 24000000;
const INACTIVITY_MS      = 12000000;
const SCROLL_THRESHOLD   = 0.95;
const GLOBAL_COOLDOWN_MS = 3600000; // 1h entre qualquer popup
// ──────────────────────────────

// ── Magma palette ──
const PURPLE = '#6660fe';
const GOLD   = '#c9a227';
const BORDER = 'rgba(255,255,255,0.1)';
const MUTED  = 'rgba(255,255,255,0.35)';
const WHITE  = '#ffffff';

const MESSAGES = [
  {
    headline: "Dados de mercado em tempo real.",
    body: "Crie uma conta gratuita para acessar o RR Magma sem interrupções — câmbio, cripto, B3 e macro em um só lugar.",
    cta: "Criar conta gratuita →",
    snooze: "Continuar por enquanto",
    color: PURPLE,
  },
  {
    headline: "Você ainda está explorando?",
    body: "Sua conta gratuita está a um clique. Sem cartão, sem compromisso — dashboards financeiros sem fricção.",
    cta: "Quero minha conta grátis →",
    snooze: "não, obrigado",
    color: PURPLE,
  },
  {
    headline: "Isso vai continuar.",
    body: "Enquanto você não tiver uma conta, vamos continuar aparecendo. Leva 30 segundos. Vale a pena.",
    cta: "Ok, vou criar minha conta →",
    snooze: "continuar sendo interrompido",
    color: GOLD,
  },
  {
    headline: "Sério, é de graça.",
    body: "Conta gratuita = dashboards sem pop-ups. Tier 1 = Magma completo. Tier 3 = Genoma + Surveillance. Simples.",
    cta: "Criar conta agora — é grátis →",
    snooze: "prefiro os pop-ups",
    color: PURPLE,
  },
  {
    headline: "Última chamada.",
    body: "Você já viu este pop-up várias vezes. Nós também sabemos. Uma conta resolve tudo isso em 30 segundos.",
    cta: "30 segundos. Criar conta. →",
    snooze: "...",
    color: PURPLE,
  },
];

const POPUP_STYLES = `
  @keyframes magmaPulseBtn {
    0%, 100% { box-shadow: 0 0 0 0 rgba(102, 96, 254, 0.55); }
    50%       { box-shadow: 0 0 0 12px rgba(102, 96, 254, 0); }
  }
  .magma-freewall-cta {
    animation: magmaPulseBtn 1.8s ease-in-out infinite;
    transition: transform 0.15s ease, background-color 0.15s ease;
  }
  .magma-freewall-cta:hover {
    animation-play-state: paused;
    transform: scale(1.02);
    background-color: #5651e8;
  }
`;

export function FreewallPopup() {
  const { isLoggedIn }            = useAuth();
  const { pathname }              = useLocation();
  const [visible, setVisible]     = useState(false);
  const [showCount, setShowCount] = useState(0);
  const lastShownAt               = useRef(0);
  const masterInterval            = useRef(null);
  const inactivityTimer           = useRef(null);
  const firstTimer                = useRef(null);
  const scrollFired               = useRef(false);
  const isLoggedInRef             = useRef(isLoggedIn);
  const visibleRef                = useRef(false);

  useEffect(() => { visibleRef.current = visible; }, [visible]);

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    if (isLoggedIn) {
      setVisible(false);
      clearInterval(masterInterval.current);
      clearTimeout(inactivityTimer.current);
      clearTimeout(firstTimer.current);
    }
  }, [isLoggedIn]);

  const AUTH_PATHS = ['/entrar', '/assinar', '/esqueci-senha', '/redefinir-senha'];

  const show = useCallback(() => {
    if (!FREEWALL_ENABLED) return;
    if (isLoggedInRef.current) return;
    if (AUTH_PATHS.includes(window.location.pathname)) return;
    const now = Date.now();
    if (now - lastShownAt.current < GLOBAL_COOLDOWN_MS) return;
    lastShownAt.current = now;
    setShowCount(c => c + 1);
    setVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => { scrollFired.current = false; }, 2000);
  }, []);

  useEffect(() => {
    if (isLoggedIn) return;
    masterInterval.current = setInterval(show, MASTER_INTERVAL_MS);
    return () => clearInterval(masterInterval.current);
  }, [isLoggedIn, show]);

  useEffect(() => {
    if (isLoggedIn) return;
    scrollFired.current = false;
    clearTimeout(firstTimer.current);
    firstTimer.current = setTimeout(show, FIRST_TRIGGER_MS);
    return () => clearTimeout(firstTimer.current);
  }, [pathname, isLoggedIn, show]);

  useEffect(() => {
    if (isLoggedIn) return;
    const onScroll = () => {
      if (isLoggedInRef.current || scrollFired.current || visibleRef.current) return;
      const pct = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (pct >= SCROLL_THRESHOLD) { scrollFired.current = true; show(); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isLoggedIn, pathname, show]);

  useEffect(() => {
    if (isLoggedIn) return;
    const reset = () => {
      if (isLoggedInRef.current) return;
      clearTimeout(inactivityTimer.current);
      if (!visibleRef.current) inactivityTimer.current = setTimeout(show, INACTIVITY_MS);
    };
    window.addEventListener("mousemove",  reset, { passive: true });
    window.addEventListener("keydown",    reset, { passive: true });
    window.addEventListener("touchstart", reset, { passive: true });
    inactivityTimer.current = setTimeout(show, INACTIVITY_MS);
    return () => {
      clearTimeout(inactivityTimer.current);
      window.removeEventListener("mousemove",  reset);
      window.removeEventListener("keydown",    reset);
      window.removeEventListener("touchstart", reset);
    };
  }, [isLoggedIn, pathname, show]);

  useEffect(() => {
    if (isLoggedIn) return;
    const onClick = (e) => {
      if (isLoggedInRef.current || visibleRef.current) return;
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("http") || href === "/entrar" || href === "/assinar") return;
      if (Math.random() < 0.4) {
        e.preventDefault();
        show();
        setTimeout(() => { window.location.href = href; }, 150);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [isLoggedIn, show]);

  // ── Early exits (after all hooks) ──
  if (!FREEWALL_ENABLED) return null;
  if (isLoggedIn || !visible) return null;

  const msgIndex      = Math.min(showCount - 1, MESSAGES.length - 1);
  const msg           = MESSAGES[Math.max(0, msgIndex)];
  const snoozeOpacity = Math.max(0.12, 0.7 - (showCount * 0.12));
  const snoozeSize    = Math.max(7, 11 - showCount);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9000,
      background: "rgba(4,2,2,0.92)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
    }}>
      <style>{POPUP_STYLES}</style>
      <div style={{
        width: "100%", maxWidth: 460,
        background: "#0a0404",
        border: `1px solid rgba(102,96,254,0.25)`,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 32px 80px rgba(0,0,0,0.8)",
      }}>
        {/* Top bar */}
        <div style={{
          background: "#070101",
          borderBottom: `2px solid ${msg.color}`,
          padding: "20px 28px",
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9, fontWeight: 700, color: msg.color,
            letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6,
          }}>RR Magma</div>
          <div style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 22, fontWeight: 700, color: WHITE, letterSpacing: "-0.02em",
          }}>{msg.headline}</div>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px 20px" }}>
          <p style={{
            fontFamily: "'Fraunces', Georgia, serif",
            fontSize: 15, color: "rgba(255,255,255,0.78)", lineHeight: 1.65, marginBottom: 20,
          }}>{msg.body}</p>

          <div style={{
            background: "rgba(255,255,255,0.03)", borderLeft: `3px solid ${msg.color}`,
            padding: "14px 18px", marginBottom: 24, borderRadius: "0 6px 6px 0",
          }}>
            {[
              "Navegação sem interrupções",
              "Câmbio, cripto, B3 e bonds",
              "Agenda econômica ao vivo",
              "RR TV financeiro",
            ].map((item, i) => (
              <div key={i} style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11, color: "rgba(255,255,255,0.65)", padding: "4px 0",
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <span style={{ color: msg.color, fontWeight: 700 }}>✓</span>{item}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Link to="/entrar" onClick={dismiss} className="magma-freewall-cta" style={{
              display: "block", textAlign: "center",
              padding: "16px", borderRadius: 6,
              background: PURPLE, color: WHITE,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13, fontWeight: 800, letterSpacing: "0.1em",
              textTransform: "uppercase", textDecoration: "none",
            }}>{msg.cta}</Link>

            <Link to="/entrar" onClick={dismiss} style={{
              display: "block", textAlign: "center",
              padding: "13px", borderRadius: 6,
              background: "transparent", color: MUTED,
              border: `1px solid ${BORDER}`,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
              textTransform: "uppercase", textDecoration: "none",
            }}>Já tenho conta — entrar</Link>

            <button onClick={dismiss} style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: snoozeSize, color: MUTED,
              letterSpacing: "0.06em", padding: "4px",
              textTransform: "lowercase", opacity: snoozeOpacity,
              transition: "opacity 0.3s ease",
            }}>{msg.snooze}</button>
          </div>
        </div>

        <div style={{
          background: "rgba(255,255,255,0.02)", borderTop: `1px solid ${BORDER}`,
          padding: "14px 28px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: MUTED, letterSpacing: "0.08em" }}>
            Acesso completo a partir de R$ 40/mês
          </span>
          <Link to="/assinar" onClick={dismiss} style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10, fontWeight: 700, color: GOLD,
            letterSpacing: "0.08em", textDecoration: "none",
          }}>Ver planos →</Link>
        </div>
      </div>
    </div>
  );
}